import {call, put, takeLatest} from '@redux-saga/core/effects';
import {ValidRecruiterCode} from 'Agent/Policies/models/RecruiterCode';
import {ValidRegionCode} from 'Agent/Policies/models/RegionCode';
import {push} from 'connected-react-router';
import {Async, AsyncError, AsyncResult, Loaded, Loading, ServerError} from 'Core/models/Async';
import {createValidatedEmail, ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {createValidatableEmptyPhone, createValidatedPhone, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {createValidatedNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {Either, fold as foldEither, isRight} from 'fp-ts/lib/Either';
import {identity} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import * as TE from 'fp-ts/lib/TaskEither';
import {all} from 'redux-saga/effects';
import {getValidationSaga} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {
  changeFieldSalesLeaderUser,
  changeRegions,
  fetchFieldSalesLeader,
  FieldSalesLeaderUserActionType,
  getRegions,
  saveFieldSalesLeaderUser,
  setEmail,
  setPhone,
  setUsername,
} from 'Users/ducks/fieldSalesLeaderUser';
import {searchRecruiterCodes} from 'Users/ducks/recruiterCode';
import {getAgentModelClaims} from 'Users/models/Claims';
import {ErrorFieldSalesLeader, ValidFieldSalesLeader} from 'Users/models/FieldSalesLeader';
import {UserRouteConstants} from 'Users/Routes';

export function* getRegionsApi(action: ReturnType<typeof getRegions>) {
  const regionCodesEither: AsyncResult<ValidRegionCode[]> = yield call(userApi.getRegionCodes);
  const asyncRegions = pipe(
    regionCodesEither,
    foldEither(identity, regions => new Loaded(regions) as Async<ValidRegionCode[]>)
  );
  yield put(changeRegions(asyncRegions));
}
export function* getRegionsSaga() {
  const regionsSaga = yield takeLatest(FieldSalesLeaderUserActionType.GET_REGIONS, getRegionsApi);
}
export function* validateEmailSaga() {
  yield takeLatest(
    FieldSalesLeaderUserActionType.VALIDATE_EMAIL,
    getValidationSaga(ValidEmail.create, setEmail, userApi.validateEmail)
  );
}

export function* validatePhoneSaga() {
  yield takeLatest(
    FieldSalesLeaderUserActionType.VALIDATE_PHONE,
    getValidationSaga(
      ValidPhoneNumber.create,
      setPhone,
      userApi.validatePhoneNumber,
      value => ValidPhoneNumber.create(value) instanceof ValidPhoneNumber
    )
  );
}

export function* validateUsernameSaga() {
  yield takeLatest(
    FieldSalesLeaderUserActionType.VALIDATE_USERNAME,
    getValidationSaga(NonEmptyString.create, setUsername, userApi.validateAllstateUsername)
  );
}
function* getFslUserFromApi(action: ReturnType<typeof fetchFieldSalesLeader>) {
  const user: AsyncResult<ValidFieldSalesLeader> = yield pipe(
    () => userApi.getUser(action.payload.userId),
    TE.chain(user =>
      pipe(
        getAgentModelClaims(user.userTypes),
        O.map(agentClaims => ({...user, ...agentClaims})),
        TE.fromOption(() => new ServerError('Could not find agent claims.') as AsyncError)
      )
    ),
    TE.map(user =>
      ValidFieldSalesLeader.create(
        NonEmptyString.create(user.firstName),
        NonEmptyString.create(user.lastName),
        createValidatedNonEmptyString(user.userName),
        user.mobilePhone ? createValidatedPhone(user.mobilePhone) : createValidatableEmptyPhone(),
        createValidatedEmail(user.email),
        ValidRecruiterCode.create(NonEmptyString.create(user.recruiterCode.value)),
        user.recruiterCode.isNewYork,
        user.recruiterCode.isAhl
      )
    )
  )();

  if (isRight(user)) {
    yield put(searchRecruiterCodes(user.right.recruiterCode.right.code.value.toUpperCase()));
    yield put(
      changeFieldSalesLeaderUser(
        new Loaded(
          ValidFieldSalesLeader.create(
            user.right.firstName,
            user.right.lastName,
            user.right.username,
            user.right.phoneNumber,
            user.right.email,
            user.right.recruiterCode,
            user.right.isNewYork,
            user.right.isAhl
          )
        )
      )
    );
  }
}
export function* getFieldSalesLeaderUserSaga() {
  yield takeLatest(FieldSalesLeaderUserActionType.FETCH_FSL_USER, getFslUserFromApi);
}
export function* saveFieldSalesLeaderUserGenerator(action: ReturnType<typeof saveFieldSalesLeaderUser>) {
  yield put(changeFieldSalesLeaderUser(new Loading()));
  const fslResult: Either<any, any> = yield call(
    userApi.saveFieldSalesLeaderUser,
    action.payload.fsl,
    action.payload.id
  );
  function* onError(errorDetails: AsyncError) {
    const errorResult = new ErrorFieldSalesLeader(
      action.payload.fsl.firstName,
      action.payload.fsl.lastName,
      action.payload.fsl.username,
      action.payload.fsl.phoneNumber,
      action.payload.fsl.email,
      action.payload.fsl.recruiterCode,
      action.payload.fsl.isNewYork,
      action.payload.fsl.isAhl,
      errorDetails
    );
    yield put(changeFieldSalesLeaderUser(new Loaded(errorResult)));
  }
  function* onSuccess() {
    yield put(push(UserRouteConstants.USERS));
  }
  yield pipe(fslResult, foldEither(onError, onSuccess as any));
}
export function* saveFSLUserSaga() {
  const savedFSLUser = yield takeLatest(
    FieldSalesLeaderUserActionType.SAVE_FSL_USER,
    saveFieldSalesLeaderUserGenerator
  );
}

export default function*() {
  yield all([
    saveFSLUserSaga(),
    getFieldSalesLeaderUserSaga(),
    validateUsernameSaga(),
    validateEmailSaga(),
    validatePhoneSaga(),
    getRegionsSaga(),
  ]);
}
